import type { Component } from 'vue';
import {
    ArchiveBoxIcon,
    BeakerIcon,
    BellIcon,
    BoltIcon,
    BookmarkIcon,
    BriefcaseIcon,
    BuildingOffice2Icon,
    BuildingStorefrontIcon,
    CalendarIcon,
    ChartBarIcon,
    CheckCircleIcon,
    ClockIcon,
    CloudIcon,
    CubeIcon,
    ExclamationTriangleIcon,
    EyeIcon,
    FireIcon,
    FlagIcon,
    FunnelIcon,
    GlobeAmericasIcon,
    HeartIcon,
    HomeIcon,
    InboxIcon,
    KeyIcon,
    LightBulbIcon,
    ListBulletIcon,
    MapPinIcon,
    MoonIcon,
    MusicalNoteIcon,
    PlayIcon,
    PuzzlePieceIcon,
    RadioIcon,
    RocketLaunchIcon,
    ServerStackIcon,
    ShieldCheckIcon,
    SignalIcon,
    SignalSlashIcon,
    SparklesIcon,
    SpeakerWaveIcon,
    Squares2X2Icon,
    StarIcon,
    SunIcon,
    TagIcon,
    TrophyIcon,
    TruckIcon,
    UserGroupIcon,
    UserIcon,
    WifiIcon,
    WrenchIcon,
    XCircleIcon,
} from '@heroicons/vue/24/outline';

/**
 * The icons and colours a saved view can wear, as Linear offers for its views.
 *
 * The backend stores the icon's key and the colour's name. It checks the colour
 * against its own list (`App\Enums\SavedViewColor`), which must match
 * SAVED_VIEW_COLORS below. It does not check the icon key, so a key this file
 * does not know falls back to the bookmark.
 *
 * A view with no icon and no colour looks the way every view did before: a
 * lime bookmark.
 */

export interface SavedViewIconOption {
    /** What the backend stores, for example "map-pin". */
    key: string;
    /** Read aloud and shown as the button's tooltip. */
    label: string;
    component: Component;
}

export const DEFAULT_SAVED_VIEW_ICON = 'bookmark';

export const SAVED_VIEW_ICONS: readonly SavedViewIconOption[] = [
    { key: 'bookmark', label: 'Bookmark', component: BookmarkIcon },
    { key: 'star', label: 'Star', component: StarIcon },
    { key: 'heart', label: 'Heart', component: HeartIcon },
    { key: 'flag', label: 'Flag', component: FlagIcon },
    { key: 'bolt', label: 'Bolt', component: BoltIcon },
    { key: 'fire', label: 'Fire', component: FireIcon },
    { key: 'bell', label: 'Bell', component: BellIcon },
    { key: 'clock', label: 'Clock', component: ClockIcon },
    { key: 'calendar', label: 'Calendar', component: CalendarIcon },
    { key: 'map-pin', label: 'Map pin', component: MapPinIcon },
    { key: 'globe', label: 'Globe', component: GlobeAmericasIcon },
    { key: 'home', label: 'Home', component: HomeIcon },
    { key: 'storefront', label: 'Storefront', component: BuildingStorefrontIcon },
    { key: 'office', label: 'Office', component: BuildingOffice2Icon },
    { key: 'user-group', label: 'People', component: UserGroupIcon },
    { key: 'user', label: 'Person', component: UserIcon },
    { key: 'signal', label: 'Signal', component: SignalIcon },
    { key: 'signal-slash', label: 'No signal', component: SignalSlashIcon },
    { key: 'wifi', label: 'Wi-Fi', component: WifiIcon },
    { key: 'warning', label: 'Warning', component: ExclamationTriangleIcon },
    { key: 'shield', label: 'Shield', component: ShieldCheckIcon },
    { key: 'check-circle', label: 'Check', component: CheckCircleIcon },
    { key: 'x-circle', label: 'Cross', component: XCircleIcon },
    { key: 'music-note', label: 'Music note', component: MusicalNoteIcon },
    { key: 'speaker', label: 'Speaker', component: SpeakerWaveIcon },
    { key: 'radio', label: 'Radio', component: RadioIcon },
    { key: 'play', label: 'Play', component: PlayIcon },
    { key: 'tag', label: 'Tag', component: TagIcon },
    { key: 'funnel', label: 'Funnel', component: FunnelIcon },
    { key: 'chart', label: 'Chart', component: ChartBarIcon },
    { key: 'list', label: 'List', component: ListBulletIcon },
    { key: 'grid', label: 'Grid', component: Squares2X2Icon },
    { key: 'inbox', label: 'Inbox', component: InboxIcon },
    { key: 'archive', label: 'Archive', component: ArchiveBoxIcon },
    { key: 'cube', label: 'Cube', component: CubeIcon },
    { key: 'server', label: 'Server', component: ServerStackIcon },
    { key: 'truck', label: 'Truck', component: TruckIcon },
    { key: 'briefcase', label: 'Briefcase', component: BriefcaseIcon },
    { key: 'key', label: 'Key', component: KeyIcon },
    { key: 'wrench', label: 'Wrench', component: WrenchIcon },
    { key: 'beaker', label: 'Beaker', component: BeakerIcon },
    { key: 'puzzle', label: 'Puzzle', component: PuzzlePieceIcon },
    { key: 'light-bulb', label: 'Light bulb', component: LightBulbIcon },
    { key: 'eye', label: 'Eye', component: EyeIcon },
    { key: 'sparkles', label: 'Sparkles', component: SparklesIcon },
    { key: 'sun', label: 'Sun', component: SunIcon },
    { key: 'moon', label: 'Moon', component: MoonIcon },
    { key: 'cloud', label: 'Cloud', component: CloudIcon },
    { key: 'trophy', label: 'Trophy', component: TrophyIcon },
    { key: 'rocket', label: 'Rocket', component: RocketLaunchIcon },
];

export interface SavedViewColorOption {
    /** What the backend stores, for example "teal". */
    key: string;
    label: string;
    /** The icon's colour. Light mode draws the swatch's own shade; dark mode draws a lighter one. */
    text: string;
    /** The swatch's fill in the picker. */
    swatch: string;
}

export const DEFAULT_SAVED_VIEW_COLOR = 'lime';

// Written out in full, not built from the key, so Tailwind finds every class.
export const SAVED_VIEW_COLORS: readonly SavedViewColorOption[] = [
    { key: 'gray', label: 'Gray', text: 'text-zinc-500 dark:text-zinc-400', swatch: 'bg-zinc-500' },
    { key: 'red', label: 'Red', text: 'text-red-500 dark:text-red-400', swatch: 'bg-red-500' },
    { key: 'orange', label: 'Orange', text: 'text-orange-500 dark:text-orange-400', swatch: 'bg-orange-500' },
    { key: 'amber', label: 'Amber', text: 'text-amber-500 dark:text-amber-400', swatch: 'bg-amber-500' },
    { key: 'yellow', label: 'Yellow', text: 'text-yellow-400', swatch: 'bg-yellow-400' },
    { key: 'lime', label: 'Lime', text: 'text-lime-500 dark:text-lime-400', swatch: 'bg-lime-500' },
    { key: 'green', label: 'Green', text: 'text-green-500 dark:text-green-400', swatch: 'bg-green-500' },
    { key: 'teal', label: 'Teal', text: 'text-teal-500 dark:text-teal-400', swatch: 'bg-teal-500' },
    { key: 'sky', label: 'Sky', text: 'text-sky-500 dark:text-sky-400', swatch: 'bg-sky-500' },
    { key: 'blue', label: 'Blue', text: 'text-blue-500 dark:text-blue-400', swatch: 'bg-blue-500' },
    { key: 'indigo', label: 'Indigo', text: 'text-indigo-500 dark:text-indigo-400', swatch: 'bg-indigo-500' },
    { key: 'violet', label: 'Violet', text: 'text-violet-500 dark:text-violet-400', swatch: 'bg-violet-500' },
    { key: 'purple', label: 'Purple', text: 'text-purple-500 dark:text-purple-400', swatch: 'bg-purple-500' },
    { key: 'pink', label: 'Pink', text: 'text-pink-500 dark:text-pink-400', swatch: 'bg-pink-500' },
    { key: 'rose', label: 'Rose', text: 'text-rose-500 dark:text-rose-400', swatch: 'bg-rose-500' },
];

/** What a view stores about its looks. Both are null until someone picks. */
export interface SavedViewAppearance {
    icon?: string | null;
    color?: string | null;
}

const ICONS_BY_KEY = new Map(SAVED_VIEW_ICONS.map((option) => [option.key, option]));
const COLORS_BY_KEY = new Map(SAVED_VIEW_COLORS.map((option) => [option.key, option]));

/** The icon to draw: the view's own, or the bookmark when it has none or one this file does not know. */
export function savedViewIconFor(appearance: SavedViewAppearance | null | undefined): Component {
    return ICONS_BY_KEY.get(appearance?.icon ?? '')?.component ?? BookmarkIcon;
}

/** The colour classes to draw the icon in: the view's own, or lime. */
export function savedViewTintFor(appearance: SavedViewAppearance | null | undefined): string {
    return (COLORS_BY_KEY.get(appearance?.color ?? '') ?? COLORS_BY_KEY.get(DEFAULT_SAVED_VIEW_COLOR)!).text;
}
